/**
 * Report Service
 *
 * API calls for the report generation and retrieval system
 */

import { get, post } from './api';

// Types
export interface TokenUsage {
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  by_section: Record<string, { input_tokens: number; output_tokens: number }>;
}

export type ReportType = 'executive_summary' | 'full_report';

export interface ReportMetadata {
  report_id: string;
  institution_id: string;
  period: string;
  storage_path: string;
  generated_at: { _seconds: number; _nanoseconds: number };
  generated_by: string;
  sections_included: string[];
  file_size_bytes: number;
  status: 'generating' | 'completed' | 'failed';
  csv_files: string[];
  token_usage: TokenUsage | null;
  // Report type and versioning
  report_type?: ReportType; // 'executive_summary' (public) or 'full_report' (private)
  version?: number; // Auto-incrementing version number
}

export interface ReportJob {
  job_id: string;
  institution_id: string;
  period: string;
  status: 'generating' | 'completed' | 'failed';
  created_by: string;
  created_at: { _seconds: number; _nanoseconds: number };
  updated_at: { _seconds: number; _nanoseconds: number };
  error: string | null;
  report_id: string | null;
}

export interface ReportContent {
  metadata: {
    institution_id: string;
    period: string;
    generation_date: string;
    token_usage?: TokenUsage;
  };
  data: {
    currentIndicators: any[];
    currentBalance: any[];
    historicalIndicators: any[];
    historicalBalance: any[];
  };
  narratives: Record<string, string>;
}

export interface GenerateReportOptions {
  institution_id: string;
  period: string;
  sections?: {
    estructura_balance?: boolean;
    indicadores_financieros?: boolean;
  };
}

// API Functions

/**
 * Check if a cached report exists for institution+period
 */
export async function checkCachedReport(
  institutionId: string,
  period: string
): Promise<{ cached: boolean; report?: ReportMetadata }> {
  return get(`/api/reports/cache/${institutionId}/${period}`);
}

/**
 * Start background report generation
 * Returns immediately with job_id for polling
 */
export async function startReportGeneration(
  options: GenerateReportOptions
): Promise<{ job_id: string; status: 'generating' }> {
  return post('/api/reports/generate', options);
}

/**
 * Get job status (for polling during generation)
 */
export async function getJobStatus(jobId: string): Promise<ReportJob> {
  return get(`/api/reports/status/${jobId}`);
}

/**
 * Get report metadata
 */
export async function getReportMetadata(reportId: string): Promise<ReportMetadata> {
  return get(`/api/reports/${reportId}/metadata`);
}

/**
 * Get full report content
 */
export async function getReportContent(reportId: string): Promise<ReportContent> {
  return get(`/api/reports/${reportId}`);
}

/**
 * Get chart data directly from BigQuery (bypasses report cache)
 * Used by ReportEditorV2 when there's no cached report
 */
export async function getChartData(institutionId: string, period: string): Promise<ReportContent> {
  return get(`/api/chart-data/${institutionId}/${period}`);
}

/**
 * Download CSV file with authentication
 */
export async function downloadReportCSV(
  reportId: string,
  csvType: 'indicators' | 'balance_sheet' | 'historical_indicators' | 'historical_balance'
): Promise<void> {
  const { getIdToken } = await import('./auth');
  const token = await getIdToken();

  const backendUrl = import.meta.env.VITE_BACKEND_URL ?? (import.meta.env.DEV ? 'http://localhost:8080' : '');
  const url = `${backendUrl}/api/reports/${reportId}/csv/${csvType}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download CSV');
  }

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || `${csvType}_${reportId}.csv`;

  // Create blob and trigger download
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * List reports for an institution
 */
export async function listInstitutionReports(
  institutionId: string,
  limit?: number
): Promise<{ reports: ReportMetadata[] }> {
  const query = limit ? `?limit=${limit}` : '';
  return get(`/api/reports/institution/${institutionId}${query}`);
}

/**
 * List all reports (admin view)
 */
export async function listAllReports(limit?: number): Promise<{ reports: ReportMetadata[] }> {
  const query = limit ? `?limit=${limit}` : '';
  return get(`/api/reports${query}`);
}

/**
 * Track report usage
 */
export async function trackReportUsage(
  reportId: string,
  action: 'view' | 'download' | 'regenerate'
): Promise<void> {
  await post(`/api/reports/${reportId}/usage`, { action });
}

// ===========================================
// REPORT DRAFT GENERATION (for ReportEditorV2)
// ===========================================

export interface DraftGenerationOptions {
  institution_id: string;
  period: string;
  org_id: string;
  analyst_id: string;
  final_due_date?: string; // ISO date string
}

export interface DraftGenerationJob extends ReportJob {
  progress?: number;
  current_section?: string;
  message?: string;
}

/**
 * Start background draft generation for a new rating report
 * This generates all 9 sections via AI and creates a report_workflows document
 */
export async function startDraftGeneration(
  options: DraftGenerationOptions
): Promise<{ job_id: string; status: 'generating' }> {
  return post('/api/report-drafts/generate', options);
}

/**
 * Get draft generation job status (reuses the same status endpoint)
 */
export async function getDraftJobStatus(jobId: string): Promise<DraftGenerationJob> {
  return get(`/api/reports/status/${jobId}`);
}

/**
 * Poll for draft generation completion
 * Returns the completed job with report_id or throws if failed
 */
export async function pollDraftCompletion(
  jobId: string,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    onProgress?: (job: DraftGenerationJob) => void;
  } = {}
): Promise<DraftGenerationJob> {
  const { intervalMs = 2000, maxAttempts = 120, onProgress } = options; // 2s intervals, 4min max

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const job = await getDraftJobStatus(jobId);

    if (onProgress) {
      onProgress(job);
    }

    if (job.status === 'completed') {
      // Ensure report_id is present before returning
      if (job.report_id) {
        console.log('[pollDraftCompletion] Job completed with report_id:', job.report_id);
        return job;
      }
      // If status is completed but no report_id yet, wait and retry
      console.log('[pollDraftCompletion] Status completed but waiting for report_id...');
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      continue;
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'Draft generation failed');
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Draft generation timed out');
}

/**
 * Poll for job completion
 * Returns the completed job or throws if failed
 */
export async function pollJobCompletion(
  jobId: string,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    onProgress?: (job: ReportJob) => void;
  } = {}
): Promise<ReportJob> {
  const { intervalMs = 3000, maxAttempts = 60, onProgress } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const job = await getJobStatus(jobId);

    if (onProgress) {
      onProgress(job);
    }

    if (job.status === 'completed') {
      return job;
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'Report generation failed');
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Report generation timed out');
}

/**
 * Helper: Format token count for display
 */
export function formatTokenCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

/**
 * Helper: Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1048576) {
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

/**
 * Helper: Format Firestore timestamp to readable date
 */
export function formatReportDate(timestamp: { _seconds: number; _nanoseconds: number }): string {
  if (!timestamp?._seconds) return 'Unknown';
  const date = new Date(timestamp._seconds * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Helper: Get relative time string
 */
export function getRelativeTime(timestamp: { _seconds: number; _nanoseconds: number }): string {
  if (!timestamp?._seconds) return '';
  const now = Date.now();
  const then = timestamp._seconds * 1000;
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return formatReportDate(timestamp);
}
